import { SELECTED_Z_INDEX } from '../standard/standard.feature';
import { Layer } from '@app/modules/cop/store/cop/models';
import { Dataset, Document } from '@app/modules/core/store/models';
import { Feature, FeatureEvent, FeatureRenderOptions, MarkerUrls } from '../feature.model';
import { DatasetZIndexValues, MapDrawingContext } from '../../models';
import { MapLayerQueryBuildService } from '@app/modules/cop/services/data/map-layer-query-build.service';
import { COLOR_7P_CYAN, TransportAlarm, TransportSegment } from '@app/features/operating-picture/7P';
import { cyrb53 } from '@app/modules/core/services/hasher';
import { v4 as uuid } from 'uuid';
import { MapLabelService } from '../../label.service';
import { MapLayerClusterService } from '../../clustering';
import { API_BASE_URL } from '@app/core/constants';

export class TransportsFeature implements Feature {
  private context: MapDrawingContext;

  private dataset: Dataset;
  private document: Document;
  private layer: Layer;

  private isSelected = false;
  private isVisible = false;

  private label: string;
  private layerHasLabels = false;
  private labelService: MapLabelService;
  private documentHash: number | string = 0;
  private clusterService: MapLayerClusterService;
  private timerId: string | number | NodeJS.Timeout;
  private minLabelZoomLevel: number = 12;
  private zIndex: number = 1000;
  private mouseoutTimers: Record<string, NodeJS.Timeout> = {};

  private mainMarker: google.maps.Marker;
  private segments: TransportSegment[] = [];
  private alarms: TransportAlarm[] = [];
  private route: google.maps.Polyline;
  private routeSegments: google.maps.Polyline[] = [];

  private markerUrls: MarkerUrls;

  private trackIsDrawn = false;

  private markers: google.maps.Marker[] = [];

  render(context: MapDrawingContext, options: FeatureRenderOptions): void {
    this.context = context;

    const { layer, document } = options;
    const dataset = context.mapState.datasetsSet[layer.datasetUId];

    this.dataset = dataset;
    this.document = document;
    this.layer = layer;

    this.isSelected = !!context.mapState.selectedDocuments[document.uid];
    this.isVisible = context.isDocumentVisible(layer, document);

    this.label = MapLayerQueryBuildService.getDatasetLabelFieldDisplay(document, context.labelFields);
    this.layerHasLabels = layer.displayOptions?.labelsVisible === true;
    this.labelService = context.labelService;
    this.clusterService = context.clusterService;
    this.minLabelZoomLevel = !!layer.displayOptions?.minLabelZoom ? layer.displayOptions?.minLabelZoom : 12;

    this.markerUrls = this.buildMarkerImageUrls(context.cacheBuster);

    this.zIndex = this.isSelected ? SELECTED_Z_INDEX : DatasetZIndexValues[dataset.type][dataset.subtype];

    const hash = this.buildDocumentHash(document, this.isSelected, this.label);

    if (this.documentHash === hash) {
      return;
    }

    this.documentHash = hash;
    this.destroy();

    this.segments = JSON.parse(document?.segments ?? '[]').map(segment => new TransportSegment(segment));

    this.alarms = JSON.parse(document?.alarm_list ?? '[]')
      .map(alarm => new TransportAlarm(alarm))
      .sort((a, b) => b.date.diff(a.date));

    if (this.segments.length) {
      const currentPosition = this.segments.find(s => s.endTime === null) ?? this.segments.at(0);

      if (!!currentPosition && !!currentPosition.path.length) {
        this.mainMarker = this.addPoint(currentPosition.path.at(0), { isMain: true }, true);
      }
    }

    if (this.isSelected) {
      this.drawRoute();
    }

    if (this.layer.displayOptions?.labelsVisible === true) {
      this.showLabel();
    } else {
      this.hideLabel();
    }
  }

  destroy(): void {
    if (!!this.mainMarker) {
      const marker = this.mainMarker;

      this.labelService.deleteMarkerLabel(this.layer.uId, marker);
      this.clusterService.registerMarkerRemove(this.layer.uId, marker);
      google.maps.event.clearInstanceListeners(marker);
      marker.unbindAll();
      marker.setMap(null);

      this.mainMarker = null;
    }

    this.removeRoute();
  }

  removeRoute(): void {
    if (!this.route) {
      return;
    }

    this.route.unbindAll();
    this.route.setMap(null);
    this.route = null;

    this.routeSegments.forEach(s => {
      s.unbindAll();
      s.setMap(null);
    });

    this.routeSegments = [];

    this.markers.forEach(m => {
      google.maps.event.clearInstanceListeners(m);
      m.unbindAll();
      m.setMap(null);
      m = null;
    });

    this.markers = [];

    this.trackIsDrawn = false;
  }

  buildMarkerImageUrls(cacheBuster: number): MarkerUrls {
    return {
      normal: `${API_BASE_URL}/v1/meta/markers/seven-p-pin/0/0/0/normal/seven-p-pin_${cacheBuster}.png`,
      selected: `${API_BASE_URL}/v1/meta/markers/seven-p-pin/0/0/0/selected/seven-p-pin_${cacheBuster}.png`
    };
  }

  setHidden(): void {
    this.isVisible = false;

    if (!!this.mainMarker) {
      this.mainMarker.setVisible(false);
      this.labelService.getMarkerLabel(this.layer.uId, this.mainMarker)?.setVisible(false);
      this.clusterService.registerMarkerRemove(this.layer.uId, this.mainMarker);
    }

    this.markers.forEach(m => m.setVisible(false));
    this.routeSegments.forEach(s => s.setVisible(false));

    if (this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }
  }

  setVisible(): void {
    this.isVisible = true;

    if (!!this.mainMarker) {
      this.mainMarker.setVisible(true);

      this.clusterService.registerMarkerAdd(this.layer.uId, this.mainMarker);

      if (this.layerHasLabels === true) {
        this.showLabel();
      }
    }
  }

  setSelected(): void {
    this.isSelected = true;
    this.setMarkerSelected();
    this.drawRoute();
  }

  setUnselected(): void {
    this.isSelected = false;
    this.setMarkerNormal();
    this.removeRoute();
  }

  showLabel(): void {
    this.layerHasLabels = true;

    if (!this.label || !this.mainMarker) {
      return;
    }

    const labelOverlay = this.labelService.getMarkerLabel(this.layer.uId, this.mainMarker);

    if (!!labelOverlay) {
      this.labelService.checkMarkerLabelDisplay(this.layer.uId, this.minLabelZoomLevel, labelOverlay);
    } else {
      const newLabel = this.labelService.createLabel(
        this.mainMarker,
        this.label,
        this.layer.uId,
        this.document.uid,
        {
          left: null,
          top: -84
        },
        'map-marker-label',
        10000
      );
      this.labelService.addMarkerLabel(this.layer.uId, this.minLabelZoomLevel, newLabel);
    }
  }

  hideLabel(): void {
    this.layerHasLabels = false;

    if (!!this.mainMarker) {
      this.labelService.deleteMarkerLabel(this.layer.uId, this.mainMarker);
    }
  }

  addPoint(position: google.maps.LatLng, properties: any, shouldCluster: boolean): google.maps.Marker {
    if (!position) {
      return;
    }

    const marker = new google.maps.Marker({
      position,
      icon: {
        url: this.markerUrls[this.isSelected ? 'selected' : 'normal']
      },
      visible: this.isVisible,
      zIndex: this.zIndex ?? 3000,
      map: this.context.googleMap
    });

    const abortController = new AbortController();
    const { signal } = abortController;
    marker['__abortController'] = abortController;

    marker['__data'] = {
      id: uuid(),
      layerUId: this.layer.uId,
      datasetUId: this.dataset.uId,
      documentUId: this.document.uid,
      properties,
      shouldCluster
    };

    const mapEventService = this.context.mapEvents;

    marker.addListener('mouseover', () => {
      this.onMarkerMouseOver({
        layerUId: this.layer.uId,
        datasetUId: this.dataset.uId,
        documentUId: this.document.uid,
        feature: marker
      });
    });

    marker.addListener('mouseout', () => {
      this.onMarkerMouseOut({
        layerUId: this.layer.uId,
        datasetUId: this.dataset.uId,
        documentUId: this.document.uid,
        feature: marker
      });
    });

    marker.addListener('click', event => {
      mapEventService.onFeatureClick({
        layerUId: this.layer.uId,
        datasetUId: this.dataset.uId,
        documentUId: this.document.uid,
        feature: marker
      });
    });

    marker.addListener('dblclick', event => {
      mapEventService.onFeatureDoubleClick({
        layerUId: this.layer.uId,
        datasetUId: this.dataset.uId,
        documentUId: this.document.uid,
        feature: marker
      });
    });

    if (shouldCluster) {
      this.clusterService.registerMarkerAdd(this.layer?.uId, marker, this.document?.uid);
    }

    return marker;
  }

  onMarkerMouseOver(e: FeatureEvent): void {
    if (this.mouseoutTimers[e.documentUId]) {
      clearTimeout(this.mouseoutTimers[e.documentUId]);
      this.mouseoutTimers[e.documentUId] = null;
    }

    this.setMarkerSelected();
    this.startHoverTimer();
  }

  onMarkerMouseOut(e: FeatureEvent): void {
    const selectFn = () => {
      if (!this.isSelected) {
        this.setMarkerNormal();
        clearTimeout(this.timerId);
        this.timerId = null;
      }
    };

    const timeout = setTimeout(selectFn, 100);
    this.mouseoutTimers[e.documentUId] = timeout;
  }

  setMarkerSelected(): void {
    const marker = this.mainMarker;

    if (!marker) {
      return;
    }

    const origZIndex = marker.getZIndex();
    marker['__data'].zIndex = origZIndex;

    marker.setZIndex(SELECTED_Z_INDEX);

    marker.setIcon({ url: this.markerUrls.selected });
  }

  setMarkerNormal(): void {
    const marker = this.mainMarker;

    if (!marker) {
      return;
    }

    const zIndex = marker['__data'].zIndex;
    marker.setZIndex(zIndex);

    marker.setIcon({ url: this.markerUrls.normal });
  }

  startHoverTimer() {
    if (!!this.timerId) {
      clearTimeout(this.timerId);
      this.timerId = null;
    }

    const selectFn = () => {
      if (!this.isSelected) {
        this.setMarkerNormal();
      }
    };

    this.timerId = setTimeout(selectFn, 3000);
  }

  drawRoute(): void {
    if (this.trackIsDrawn || !this.document?.position_list) {
      return;
    }

    try {
      const geojson = JSON.parse(this.document.position_list);
      // * NOTE * Position List will always be a Feature Collection with a LineString Feature
      const path = geojson.features.at(0).geometry.coordinates.map(([lng, lat]) => new google.maps.LatLng({ lat, lng }));

      this.route = new google.maps.Polyline({
        path,
        geodesic: true,
        strokeColor: COLOR_7P_CYAN,
        strokeOpacity: 0.5,
        strokeWeight: 4,
        map: this.context.googleMap,
        zIndex: 9999
      });

      // match properties for what marker generation expects for alarms
      const routeEnds = [
        {
          alertColor: COLOR_7P_CYAN,
          alertIcon: 'icon-small_solid_package-2',
          point: path.at(0),
          id: `${this.document.uid}_first`
        },
        {
          alertColor: COLOR_7P_CYAN,
          alertIcon: 'icon-small_solid_package-2',
          point: path.at(-1),
          id: `${this.document.uid}_last`
        }
      ];

      this.routeSegments = this.segments.map(
        ({ path, alertColor: strokeColor }) =>
          new google.maps.Polyline({
            path,
            geodesic: true,
            strokeColor,
            strokeOpacity: 1,
            strokeWeight: 4,
            map: this.context.googleMap,
            zIndex: SELECTED_Z_INDEX
          })
      );

      this.markers = [...routeEnds, ...this.alarms].map(a => {
        const color = a.alertColor.replace('#', '');
        const icon = a.alertIcon.replace('icon-', '').replaceAll('_', '-');

        const marker = new google.maps.Marker({
          position: a.point,
          icon: {
            url: `/api/v1/meta/markers/sample/dot_filled_${color}_TOPO-${icon}_normal.svg`,
            size: new google.maps.Size(40, 40),
            anchor: new google.maps.Point(20, 20)
          },
          visible: this.isVisible,
          zIndex: SELECTED_Z_INDEX,
          map: this.context.googleMap
        });

        marker['__data'] = {
          id: uuid(),
          layerUId: this.layer.uId,
          datasetUId: this.dataset.uId,
          documentUId: this.document.uid,
          properties: {
            alert_id: a.id
          },
          shouldCluster: false
        };

        return marker;
      });
    } catch (err) {
      console.error(err);
      return;
    }

    this.trackIsDrawn = true;
  }

  buildDocumentHash(document: Document, isSelected: boolean, label: string): number {
    const positionList = document?.position_list ?? '';
    const segments = document?.segments ?? '';
    const alarms = document?.alarm_list ?? '';

    const stringToHash = [positionList, segments, alarms, isSelected, label ?? ''].join();

    const hash = cyrb53(stringToHash);

    return hash;
  }
}
